import { Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  AutoComplete,
  AutoCompleteModule,
  AutoCompleteSelectEvent
} from 'primeng/autocomplete';

import { GenericColumnConfiguration } from 'src/app/models/GenericColumnConfiguration';
import { SHARED_IMPORTS } from 'src/app/utils/other/standalone-import';

type AutoCompleteCompleteEvent = {
  originalEvent: Event;
  query: string;
};

@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.scss',
  standalone: true,
  imports: [SHARED_IMPORTS, AutoCompleteModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true
    }
  ]
})
export class AutocompleteComponent implements ControlValueAccessor, OnInit {
  @ViewChild('autocomplete') autocomplete!: AutoComplete;

  @Input({ required: true }) columnConfiguration!: GenericColumnConfiguration;

  @Input() id?: string | undefined = undefined;

  @Input() name?: string | undefined = undefined;

  @Input() placeholder?: string | undefined = undefined;

  @Input() defaultValue?: Record<string, unknown> | undefined = undefined;

  ngOnInit(): void {
    const refKey = this.columnConfiguration.referringKey;
    const value = this.defaultValue;

    if (refKey && value && typeof value === 'object' && refKey in value) {
      this.value = String(value[refKey]);

      setTimeout(() => {
        this.autocomplete.updateModel(String(value[refKey]));
      }, 50); // Delay to ensure the model is updated
    }
  }

  value: string | null = null;

  onChange: (value: string | null) => void = () => {};

  onTouched: () => void = () => {};

  filteredOptions: unknown[] = [];

  isDisabled = false;

  keydownEventListener = null;

  /**
   * Filters the autocomplete options based on the input query.
   * If no options or referringKey are provided, it logs a warning and returns.
   * If the query is empty, it sets the filtered options to all available options.
   * Otherwise, it filters the options based on the query.
   *
   * @param {AutoCompleteCompleteEvent} event - The autocomplete complete event containing the query.
   * @returns {void}
   */
  filter(event: AutoCompleteCompleteEvent): void {
    this.handleInputChange(event.query);

    if (
      !this.columnConfiguration.options ||
      !this.columnConfiguration.referringKey
    ) {
      console.warn(
        '[AutocompleteComponent] No options or referringKey provided'
      );

      return;
    }

    if (!event.query) {
      this.filteredOptions = [...this.columnConfiguration.options];

      return;
    }

    const filtered: unknown[] = [];

    const options = this.columnConfiguration.options ?? [];

    for (let i = 0; i < options.length; i++) {
      const value = String(
        options[i][this.columnConfiguration.referringKey]
      ).toLowerCase();

      if (value.startsWith(event.query.toLowerCase())) {
        filtered.push(options[i]);
      }
    }

    this.filteredOptions = filtered;
  }

  /**
   * Writes a new value to the component.
   *
   * @param {string | null} value - The new value to be written.
   * @returns {void}
   */
  writeValue(value: string | null): void {
    this.value = value;
  }

  /**
   * Registers a callback function to be called when the value changes.
   *
   * @param {(value: string | null) => void} fn - The callback function.
   * @returns {void}
   */
  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function to be called when the component is touched.
   *
   * @param {() => void} fn - The callback function.
   * @returns {void}
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the component.
   *
   * @param {boolean} isDisabled - The disabled state.
   * @returns {void}
   */
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Handles the selection of an autocomplete option.
   * If no referringKey is provided, it logs a warning and returns.
   * Otherwise, it updates the input value based on the selected option.
   *
   * @param {AutoCompleteSelectEvent} event - The autocomplete select event containing the selected value.
   * @returns {void}
   */
  handleOptionSelect(event: AutoCompleteSelectEvent): void {
    if (!this.columnConfiguration.referringKey) {
      console.warn(
        '[AutocompleteComponent] No referringKey provided for column configuration'
      );

      return;
    }

    this.handleInputChange(event.value[this.columnConfiguration.referringKey]);
  }

  /**
   * Handles changes to the input value.
   * Updates the component's value and calls the registered onChange callback.
   * If the input is empty, it calls the registered onTouched callback.
   *
   * @param {string} input - The new input value.
   * @returns {void}
   */
  handleInputChange(input: string): void {
    this.value = input;
    this.onChange(this.value);

    if (!input) {
      this.onTouched();
    }
  }

  /**
   * Handles the focus event on the input element.
   * Adds a keydown event listener to the input element.
   *
   * @returns {void}
   */
  handleFocus(): void {
    const inputEl = this.autocomplete.inputEL
      ?.nativeElement as HTMLInputElement;
    inputEl.addEventListener('keydown', this.handleKeydown);
  }

  /**
   * Handles the blur event on the input element.
   * Removes the keydown event listener from the input element.
   *
   * @returns {void}
   */
  handleBlur(): void {
    const inputEl = this.autocomplete.inputEL
      ?.nativeElement as HTMLInputElement;

    inputEl.removeEventListener('keydown', this.handleKeydown);
  }

  /**
   * Handles the keydown event on the input element.
   * If the Escape key is pressed, it prevents the default action and hides the autocomplete dropdown.
   * This method is defined as an arrow function to maintain the class context.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   * @returns {void}
   */
  handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.autocomplete.hide();
    }
  };
}
